/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file MailUiKit.js
 * @description
 * Mail-specific presentational primitives that complement the shared
 * ManagementUI kit. Everything here is theme-aware (useManagementTheme) and
 * speaks the "Happy Premium" language — white rounded cards with a hairline
 * border, soft tinted chips, pill tabs and an orange accent (text on an orange
 * fill is always white) — on both iOS and Android:
 *
 *  - {@link MailListItem}     swipeable premium message card (avatar, unread dot, star, attach)
 *  - {@link RecipientInput}   To/Cc/Bcc chips with backend autocomplete
 *  - {@link MailHtmlBody}     sandboxed WebView email renderer (links, images,
 *                             scale-to-fit, robust auto-height, shimmer overlay)
 *  - {@link MailPlainBody}    paragraph/quote/link aware plain-text fallback
 *  - {@link MailBodySkeleton} body-shaped shimmer used while the reader paints
 *  - {@link AttachmentTile}   attachment chip with size + download affordance
 *  - {@link FolderPill}       horizontal folder/label selector pill
 *  - {@link AccountPill}      account switcher chip
 *  - helpers: initials(), formatMailTime(), mailAddressLabel(), openMailLink()
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    ActivityIndicator,
    Animated,
    useWindowDimensions,
    Easing,
    Linking,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View,
} from 'react-native';
import {Swipeable} from 'react-native-gesture-handler';
import {WebView} from 'react-native-webview';
import {LinearGradient} from 'expo-linear-gradient';
import * as WebBrowser from 'expo-web-browser';
import Icon from '../management/SafeIcon';
import {useManagementTheme, withAlpha} from '../../helpers/managementTheme';
import {searchMailRecipients} from '../../services/mailApi';
import {PremiumSheet, SheetOption, TILE_GRADIENTS} from '../settings/SettingsUiKit';
import {colorHex, messageColorId, resolveRowLabels} from '../../helpers/mailOrganize';
import {resolveReadReceipt} from '../../helpers/mailReadReceipt';

/* ───────────────────────────── helpers ───────────────────────────── */

/** First-letter initials from a display name or e-mail. @param {Object|string} who */
export const initials = (who) => {
    const name = typeof who === 'string' ? who : (who?.name || who?.email || '');
    const parts = String(name).trim().split(/[\s@.]+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
};

/** A stable accent colour derived from an address (avatar tint). */
const AVATAR_TONES = ['#EA580C', '#7C3AED', '#0D9488', '#DB2777', '#B45309', '#16A34A', '#E11D48', '#475569'];
/** Matching two-stop gradients for a richer, more premium avatar fill. */
const AVATAR_GRADIENTS = {
    '#EA580C': ['#FB923C', '#C2410C'],
    '#7C3AED': ['#A78BFA', '#6D28D9'],
    '#DB2777': ['#F472B6', '#BE185D'],
    '#D97706': ['#FBBF24', '#B45309'],
    '#16A34A': ['#34D399', '#15803D'],
    '#E11D48': ['#FB7185', '#BE123C'],
    '#475569': ['#94A3B8', '#334155'],
    '#0D9488': ['#2DD4BF', '#0F766E'],
};
export const avatarTone = (who) => {
    const seed = String((typeof who === 'string' ? who : (who?.email || who?.name || '')) || '');
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
    return AVATAR_TONES[hash % AVATAR_TONES.length];
};
/** Two-stop gradient pair for an address (premium avatar fill). */
export const avatarGradient = (who) => {
    const base = avatarTone(who);
    return AVATAR_GRADIENTS[base] || [base, base];
};

/**
 * Linear-blend two 6-digit hex colours. Used to lift the avatar glyph colour
 * toward white in dark mode so initials stay legible on the tinted disc.
 * @param {string} hex    Base colour (`#RRGGBB`).
 * @param {string} target Colour to blend toward (`#RRGGBB`).
 * @param {number} ratio  0 → `hex`, 1 → `target`.
 * @returns {string} Blended `#RRGGBB` colour (falls back to `hex` on bad input).
 */
const mixHex = (hex, target, ratio) => {
    const parse = (c) => {
        const s = String(c || '').replace('#', '');
        if (s.length !== 6) return null;
        const n = parseInt(s, 16);
        if (Number.isNaN(n)) return null;
        return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    };
    const a = parse(hex);
    const b = parse(target);
    if (!a || !b) return hex;
    const ch = (i) => Math.round(a[i] + (b[i] - a[i]) * ratio);
    return `#${[ch(0), ch(1), ch(2)].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

/** Human display label for a single address. @param {Object} a */
export const mailAddressLabel = (a) => {
    if (!a) return '';
    if (typeof a === 'string') return a;
    return a.name || a.email || '';
};

/** Compact relative/absolute time used in list rows. @param {string} value ISO */
export const formatMailTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const sameDay = date.toDateString() === now.toDateString();
    if (sameDay) {
        return date.toLocaleTimeString('hu-HU', {hour: '2-digit', minute: '2-digit'});
    }
    const diffDays = Math.floor((now - date) / 86400000);
    if (diffDays < 7) {
        return date.toLocaleDateString('hu-HU', {weekday: 'short'});
    }
    return date.toLocaleDateString('hu-HU', {month: 'short', day: 'numeric'});
};

/* ───────────────────────────── Avatar ───────────────────────────── */

/**
 * Round sender avatar: initials on a soft tint of a stable per-address tone (the
 * same calm treatment as the shared `Avatar`). `selected` swaps in a solid
 * brand-orange disc with a white check; `ring` draws an accent ring around it.
 * @param {{who:(Object|string), size?:number, selected?:boolean, onPress?:()=>void, ring?:string}} props
 * @returns {JSX.Element}
 */
export const MailAvatar = ({who, size = 46, selected, onPress, ring}) => {
    const m = useManagementTheme();
    const grad = avatarGradient(who);
    const discStyle = [styles.avatar, {width: size, height: size, borderRadius: size / 2}];
    // Solid gradient disc + white initials reads clearly at a glance (Gmail/Superhuman style);
    // the tone is still derived deterministically from the sender address.
    const inner = selected ? (
        <View style={[discStyle, {backgroundColor: m.colors.primary}]}>
            <Icon name="check" size={size * 0.5} color="#FFFFFF" />
        </View>
    ) : (
        <LinearGradient colors={m.isDark ? [mixHex(grad[0], '#000000', 0.15), mixHex(grad[1], '#000000', 0.15)] : grad} start={{x: 0, y: 0}} end={{x: 1, y: 1}} style={discStyle}>
            <Text style={[styles.avatarText, {fontSize: size * 0.38, color: '#FFFFFF'}]}>{initials(who)}</Text>
        </LinearGradient>
    );
    // An unread ring gives the row a focused, premium accent without a heavy dot.
    const body = ring ? (
        <View style={[styles.avatarRing, {width: size + 6, height: size + 6, borderRadius: (size + 6) / 2, borderColor: ring}]}>
            {inner}
        </View>
    ) : inner;
    if (onPress) {
        return <TouchableOpacity onPress={onPress} activeOpacity={0.7}>{body}</TouchableOpacity>;
    }
    return body;
};

/* ───────────────────────── Label / category chip ────────────────── */

/** Tiny coloured label/category chip shown on rows and in the reader. */
export const LabelChip = ({label, color, small}) => {
    const m = useManagementTheme();
    const tint = color || m.colors.info;
    return (
        <View style={[styles.labelChip, small && styles.labelChipSm, {backgroundColor: tint + (m.isDark ? '28' : '1A'), borderColor: tint + '55'}]}>
            <View style={[styles.labelDot, {backgroundColor: tint}]} />
            <Text style={[styles.labelChipText, small && {fontSize: 10}, {color: tint}]} numberOfLines={1}>{label}</Text>
        </View>
    );
};

/**
 * "Opened by the recipient" pill for mail we sent with open tracking on: a green eye with
 * "First opened <time>" once genuinely read, a muted crossed eye while unopened, an info shield
 * when only Apple Mail Privacy Protection prefetches were seen.
 * @param {{receipt:{state:string, firstOpenedAt:(string|null)}, t?:Function}} props
 * @returns {JSX.Element}
 */
export const ReadReceiptPill = ({receipt, t}) => {
    const m = useManagementTheme();
    const read = receipt?.state === 'read';
    const privacy = receipt?.state === 'privacy';
    const tint = read ? (m.colors.success || '#16A34A') : privacy ? m.colors.info : m.colors.muted;
    const when = read ? formatMailTime(receipt.firstOpenedAt) : '';
    const label = read
        ? (typeof t === 'function'
            ? t('mail_tracking_first_opened', {when, defaultValue: `First opened ${when}`})
            : `First opened ${when}`)
        : privacy
            ? tr(t, 'mail_tracking_privacy', 'Delivered · Apple hides opens')
            : tr(t, 'mail_tracking_pending', 'Not opened yet');

    return (
        <View
            style={[styles.readPill, {backgroundColor: withAlpha(tint, read ? (m.isDark ? 0.2 : 0.12) : 0.08), borderColor: withAlpha(tint, read ? 0.45 : 0.3)}]}
            accessibilityLabel={label}
        >
            <Icon name={read ? 'eye-check-outline' : privacy ? 'shield-outline' : 'eye-off-outline'} size={12} color={tint} />
            <Text style={[styles.readPillText, {color: tint}]} numberOfLines={1}>{label}</Text>
        </View>
    );
};

/* ───────────────────────── Message list row ─────────────────────── */

/**
 * Swipeable premium mail row — a rounded white card (radius 22, hairline border,
 * very soft shadow) with a 3-line layout (bold sender + time, subject, preview),
 * a 42px tinted avatar, a small orange unread dot over a faint orange unread
 * tint, an inline paperclip glyph when the message has
 * attachments, a compact star toggle, tiny label chips and (when present)
 * small tinted AI triage chips: `message.ai.category`
 * always, `message.ai.priority` only for `high`/`urgent` (see
 * {@link module:services/mailApi.triageMailMessage}). Left swipe → archive,
 * right swipe → trash (both optional).
 * @param {{message:Object, selected?:boolean, selectionMode?:boolean,
 *          onPress?:()=>void, onLongPress?:()=>void, onToggleStar?:()=>void,
 *          onArchive?:()=>void, onTrash?:()=>void, onToggleRead?:()=>void,
 *          collabMeta?:React.ReactNode, labelIndex?:Object, t?:Function}} props `labelIndex` (id -> {name,color},
 *        see helpers/mailOrganize) resolves label ids to chips; `message.colorMark` paints a leading colour stripe. `t` is optional; without it the row falls back
 *        to English for its own copy (subject fallback + AI chip labels).
 * @returns {JSX.Element}
 */
export const MailListItem = ({
    t,
    message,
    selected,
    selectionMode,
    onPress,
    onLongPress,
    onToggleStar,
    onArchive,
    onTrash,
    onToggleRead,
    collabMeta,
    labelIndex,
}) => {
    const m = useManagementTheme();
    const from = useMemo(() => {
        const list = Array.isArray(message?.from) ? message.from : [];
        return list[0] || {name: message?.fromName, email: message?.fromEmail};
    }, [message]);
    const isOutgoing = message?.direction === 'outgoing' || message?.folderKey === 'sent';
    const primary = isOutgoing
        ? (Array.isArray(message?.to) && message.to.length ? mailAddressLabel(message.to[0]) : mailAddressLabel(from))
        : mailAddressLabel(from);
    const unread = !message?.isRead && !isOutgoing;
    const attachCount = Array.isArray(message?.attachments) ? message.attachments.length : 0;
    const hasAttachments = attachCount > 0;
    // Read receipt for mail we sent with open tracking on: a green "first opened <time>" pill with an
    // eye once a recipient genuinely opened it, a muted crossed-eye pill while it is still unopened.
    const readReceipt = resolveReadReceipt(message);
    const aiCategory = message?.ai?.category;
    const aiPriority = message?.ai?.priority;
    const showAiPriority = aiPriority === 'high' || aiPriority === 'urgent';
    const aiPriorityColor = aiPriority === 'urgent' ? m.colors.danger : m.colors.warning;
    const aiCategoryLabel = aiCategory ? tr(t, `mail_ai_cat_${aiCategory}`, String(aiCategory)) : '';
    const aiPriorityLabel = showAiPriority ? tr(t, `mail_ai_prio_${aiPriority}`, String(aiPriority)) : '';
    const colorId = messageColorId(message);
    const stripeColor = colorId ? colorHex(colorId, m.isDark) : '';
    const rowLabelInfo = useMemo(() => resolveRowLabels(message, labelIndex, 3), [message, labelIndex]);
    const rowLabels = rowLabelInfo.chips;
    const queued = isOutgoing && message?.status === 'queued';
    const failed = isOutgoing && message?.status === 'failed';

    const renderRightActions = useCallback(() => (
        <View style={styles.swipeWrap}>
            <View style={[styles.swipeAction, {backgroundColor: m.colors.danger}]}>
                <Icon name="trash-can-outline" size={22} color="#FFFFFF" />
            </View>
        </View>
    ), [m.colors.danger]);

    const renderLeftActions = useCallback(() => (
        <View style={styles.swipeWrap}>
            <View style={[styles.swipeAction, {backgroundColor: m.colors.success}]}>
                <Icon name="archive-outline" size={22} color="#FFFFFF" />
            </View>
        </View>
    ), [m.colors.success]);

    const whoForAvatar = isOutgoing ? (message?.to?.[0] || from) : from;
    const row = (
        <Pressable
            onPress={onPress}
            onLongPress={onLongPress}
            style={({pressed}) => [
                styles.item,
                m.shadow(1),
                {
                    backgroundColor: selected ? m.colors.chipBg
                        : pressed ? m.colors.surfaceAlt
                            : unread ? (m.isDark ? '#1B2238' : '#FFF8F2') : m.colors.surface,
                    borderColor: selected ? m.colors.primary
                        : unread ? withAlpha(m.colors.primary, m.isDark ? 0.34 : 0.22) : m.colors.border,
                },
            ]}
        >
            {/* Leading-edge unread accent dot — a fixed gutter keeps every row aligned. */}
            {!!stripeColor && <View pointerEvents="none" style={[styles.colorStripe, {backgroundColor: stripeColor}]} />}
            <View style={styles.itemLead}>
                {unread && !selected && <View style={[styles.unreadDot, {backgroundColor: m.colors.primary}]} />}
            </View>
            {selectionMode ? (
                <MailAvatar who={whoForAvatar} size={44} selected={selected} onPress={onLongPress} />
            ) : (
                <MailAvatar who={whoForAvatar} size={44} />
            )}
            <View style={styles.itemMain}>
                <View style={styles.itemTopRow}>
                    <Text style={[styles.itemFrom, {color: m.colors.heading, fontWeight: unread ? '800' : '600'}]} numberOfLines={1}>
                        {primary || '—'}
                    </Text>
                    <View style={styles.itemTopRight}>
                        {hasAttachments && <Icon name="paperclip" size={13} color={m.colors.muted} />}
                        <TouchableOpacity onPress={onToggleStar} hitSlop={8} style={styles.itemStarBtn} accessibilityRole="button">
                            <Icon name={message?.isStarred ? 'star' : 'star-outline'} size={16} color={message?.isStarred ? '#F59E0B' : m.colors.muted} />
                        </TouchableOpacity>
                        <Text style={[styles.itemTime, {color: unread ? m.colors.primary : m.colors.muted}]} numberOfLines={1}>
                            {formatMailTime(message?.receivedAt || message?.sentAt || message?.createdAt)}
                        </Text>
                    </View>
                </View>
                <View style={styles.itemTopRow}>
                    <Text style={[styles.itemSubject, {color: unread ? m.colors.heading : m.colors.text, fontWeight: unread ? '800' : '600'}]} numberOfLines={1}>
                        {message?.subject || tr(t, 'mail_no_subject', '(no subject)')}
                    </Text>
                    {queued && <View style={[styles.statusDot, {backgroundColor: m.colors.warning}]} />}
                    {failed && <View style={[styles.statusDot, {backgroundColor: m.colors.danger}]} />}
                </View>
                <View style={styles.itemBottomRow}>
                    <Text style={[styles.itemPreview, {color: m.colors.subText}]} numberOfLines={1}>
                        {message?.previewText || ''}
                    </Text>
                </View>
                {(readReceipt || rowLabels.length > 0 || aiCategoryLabel || aiPriorityLabel) && (
                    <View style={styles.itemLabels}>
                        {readReceipt && <ReadReceiptPill receipt={readReceipt} t={t} />}
                        {rowLabels.map((l) => <LabelChip key={l.label} label={l.label} color={l.color} small />)}
                        {rowLabelInfo.extra > 0 && <LabelChip key="label-extra" label={`+${rowLabelInfo.extra}`} small />}
                        {!!aiCategoryLabel && <LabelChip key="ai-category" label={aiCategoryLabel} small />}
                        {!!aiPriorityLabel && <LabelChip key="ai-priority" label={aiPriorityLabel} color={aiPriorityColor} small />}
                    </View>
                )}
                {/* Shared-mailbox collaboration meta (replied-by, assignee, team tags, snooze) — optional. */}
                {collabMeta || null}
            </View>
        </Pressable>
    );

    if (selectionMode) {
        return row;
    }
    return (
        <Swipeable
            renderRightActions={onTrash ? renderRightActions : undefined}
            renderLeftActions={onArchive ? renderLeftActions : undefined}
            onSwipeableRightOpen={onTrash}
            onSwipeableLeftOpen={onArchive}
            overshootRight={false}
            overshootLeft={false}
        >
            {row}
        </Swipeable>
    );
};

/* ───────────────────────── Recipient input ──────────────────────── */

const isValidEmail = (value) => /\S+@\S+\.\S+/.test(String(value || '').trim());

/**
 * To/Cc/Bcc chips field with backend autocomplete.
 * @param {{label:string, value:Array, onChange:(next:Array)=>void, autoFocus?:boolean}} props
 */
export const RecipientInput = ({label, value, onChange, autoFocus}) => {
    const m = useManagementTheme();
    const [text, setText] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const debounceRef = useRef(null);
    const recipients = Array.isArray(value) ? value : [];

    useEffect(() => {
        if (!text || text.length < 2) {
            setSuggestions([]);
            return undefined;
        }
        if (debounceRef.current) globalThis.clearTimeout(debounceRef.current);
        debounceRef.current = globalThis.setTimeout(async () => {
            try {
                const res = await searchMailRecipients(text, 8);
                const list = Array.isArray(res?.results || res) ? (res.results || res) : [];
                setSuggestions(list);
            } catch (_e) {
                setSuggestions([]);
            }
        }, 250);
        return () => debounceRef.current && globalThis.clearTimeout(debounceRef.current);
    }, [text]);

    const addRecipient = useCallback((recipient) => {
        const email = (recipient?.email || recipient || '').toString().trim();
        if (!email) return;
        if (recipients.some((r) => (r.email || '').toLowerCase() === email.toLowerCase())) {
            setText('');
            setSuggestions([]);
            return;
        }
        onChange([...recipients, {name: recipient?.name || '', email}]);
        setText('');
        setSuggestions([]);
    }, [recipients, onChange]);

    const removeRecipient = useCallback((email) => {
        onChange(recipients.filter((r) => (r.email || '') !== email));
    }, [recipients, onChange]);

    const commitText = useCallback(() => {
        const candidate = text.trim().replace(/[,;]\s*$/, '');
        if (isValidEmail(candidate)) {
            addRecipient({email: candidate});
        }
    }, [text, addRecipient]);

    return (
        <View style={[styles.recipientWrap, {borderBottomColor: m.colors.borderSoft}, suggestions.length > 0 && styles.recipientWrapOpen]}>
            <View style={styles.recipientRow}>
            <Text style={[styles.recipientLabel, {color: m.colors.subText}]}>{label}</Text>
            <View style={styles.recipientField}>
                {recipients.map((r) => (
                    <TouchableOpacity
                        key={r.email}
                        onPress={() => removeRecipient(r.email)}
                        style={[styles.recipientChip, {backgroundColor: m.colors.chipBg, borderColor: withAlpha(m.colors.primary, 0.28)}]}
                        activeOpacity={0.7}
                    >
                        <MailAvatar who={r} size={18} />
                        <Text style={[styles.recipientChipText, {color: m.colors.heading, marginLeft: 6}]} numberOfLines={1}>
                            {r.name || r.email}
                        </Text>
                        <Icon name="close" size={13} color={m.colors.muted} style={{marginLeft: 4}} />
                    </TouchableOpacity>
                ))}
                <TextInput
                    value={text}
                    onChangeText={(t) => {
                        if (/[,;]\s*$/.test(t)) {
                            setText(t);
                            commitText();
                        } else {
                            setText(t);
                        }
                    }}
                    onSubmitEditing={commitText}
                    onBlur={commitText}
                    autoFocus={autoFocus}
                    autoCapitalize="none"
                    autoCorrect={false}
                    keyboardType="email-address"
                    placeholder=""
                    placeholderTextColor={m.colors.placeholder}
                    style={[styles.recipientInput, {color: m.colors.heading}]}
                />
            </View>
            </View>
            {suggestions.length > 0 && (
                <View style={[styles.suggestBox, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(2)]}>
                    {suggestions.map((s) => (
                        <TouchableOpacity key={`${s.email}-${s.source || ''}`} onPress={() => addRecipient(s)} style={styles.suggestRow} activeOpacity={0.7}>
                            <MailAvatar who={s} size={34} />
                            <View style={{flex: 1, marginLeft: 10}}>
                                <Text style={[styles.suggestName, {color: m.colors.heading}]} numberOfLines={1}>{s.name || s.email}</Text>
                                {!!s.name && <Text style={[styles.suggestEmail, {color: m.colors.subText}]} numberOfLines={1}>{s.email}</Text>}
                            </View>
                            {!!s.contactType && <Text style={[styles.suggestType, {color: m.colors.muted}]}>{s.contactType}</Text>}
                        </TouchableOpacity>
                    ))}
                </View>
            )}
        </View>
    );
};

/* ───────────────────────── HTML body renderer ───────────────────── */

/**
 * Fully transparent 1x1 GIF used as the temporary `src` of a blocked remote
 * image. The *visible* placeholder comes from CSS (`.__blockedimg`), so the
 * reader shows an obvious "image blocked" tile instead of a broken-image icon.
 * @type {string}
 */
const BLOCKED_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

/**
 * Neutralise every remote `url(...)` reference of a CSS fragment by prefixing
 * its scheme with an unknown one, so the WebView can never issue the request.
 * {@link MailHtmlBody} reverses this in place — without reloading the mail —
 * when the user allows images.
 * @param {string} css Raw CSS text (an inline `style` attribute or a `<style>` body).
 * @returns {string} The same CSS with every http(s) `url()` rendered inert.
 */
const neutraliseCssUrls = (css) => String(css || '').replace(/url\(\s*(['"]?)\s*(https?:)/gi, 'url($1blocked-$2');

/**
 * Strip every remote asset reference from an e-mail body so rendering it makes
 * no outbound request. Covers the spots a naive `src`-only filter misses:
 * `<img src>`, `srcset` (both `<img>` and `<source>`), `poster`, the legacy
 * `background` attribute, inline `style="…url(…)"` and `<style>` blocks.
 * Every original value survives in a `data-blocked-*` attribute (or behind the
 * `blocked-` scheme prefix) so the document can be un-blocked in place.
 * @param {string} html Raw e-mail HTML.
 * @returns {string} Privacy-safe HTML.
 */
const blockRemoteAssets = (html) => String(html || '')
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, (block) => neutraliseCssUrls(block))
    .replace(/(<img\b[^>]*?)\ssrc\s*=\s*("|')(\s*https?:\/\/[^"']*)\2/gi, `$1 data-blocked-src=$2$3$2 src="${BLOCKED_PIXEL}"`)
    .replace(/(<(?:img|source|video)\b[^>]*?)\ssrcset\s*=\s*("|')([^"']*)\2/gi, '$1 data-blocked-srcset=$2$3$2')
    .replace(/(<video\b[^>]*?)\sposter\s*=\s*("|')(\s*https?:\/\/[^"']*)\2/gi, '$1 data-blocked-poster=$2$3$2')
    .replace(/(<[a-z][^>]*?)\sbackground\s*=\s*("|')(\s*https?:\/\/[^"']*)\2/gi, '$1 data-blocked-background=$2$3$2')
    // Two passes so a double-quoted style may contain apostrophes (and vice
    // versa) — a single [^"']* class silently skips `style="background:url('…')"`.
    .replace(/(\sstyle\s*=\s*")([^"]*)"/gi, (_all, head, css) => `${head}${neutraliseCssUrls(css)}"`)
    .replace(/(\sstyle\s*=\s*')([^']*)'/gi, (_all, head, css) => `${head}${neutraliseCssUrls(css)}'`);

/**
 * Build the sandboxed document rendered inside the reader WebView.
 *
 * Beyond styling, the injected script is what makes the reader behave like a
 * real mail client: it scales fixed-width desktop e-mails down to fit instead
 * of clipping them, keeps re-measuring so a late image can never leave a mail
 * "half rendered", hands every link tap back to React Native, repairs
 * unreadable colour pairs in dark mode (never inverting images) and can
 * un-block remote content in place.
 *
 * @param {string} html Raw e-mail HTML (already sanitised server-side).
 * @param {{blockRemote?:boolean, isDark?:boolean, pad?:number}} options Render options.
 * @returns {string} A complete HTML document for `WebView.source.html`.
 */
const buildHtmlDocument = (html, {blockRemote, isDark, pad = 14}) => {
    const processed = blockRemote ? blockRemoteAssets(html) : String(html || '');
    const bg = isDark ? '#0F172A' : '#FFFFFF';
    const fg = isDark ? '#E2E8F0' : '#1E293B';
    const muted = isDark ? '#94A3B8' : '#64748B';
    const rule = isDark ? '#1E293B' : '#E5E9F0';
    const quoteBar = isDark ? '#334155' : '#CBD5E1';
    const codeBg = isDark ? '#111C31' : '#F1F5F9';
    const blockedBg = isDark ? '#182338' : '#F1F5F9';
    const blockedIcon = isDark ? '%2394A3B8' : '%2364748B';

    return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5"/>
<style>
  html{-webkit-text-size-adjust:100%;background:${bg};}
  body{margin:0;padding:${pad}px;background:${bg};color:${fg};overflow-x:hidden;
       font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;
       font-size:16px;line-height:1.55;word-break:break-word;overflow-wrap:anywhere;}
  *{box-sizing:border-box;}
  #__mailroot{display:block;}
  img{max-width:100%!important;height:auto;border:0;vertical-align:middle;}
  a{color:#F97316;text-decoration:underline;}
  a:active{opacity:.55;}
  table{max-width:100%!important;border-collapse:collapse;}
  td,th{word-break:break-word;}
  pre,code,kbd,samp{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:14px;}
  pre{white-space:pre-wrap;word-wrap:break-word;background:${codeBg};padding:10px 12px;border-radius:10px;overflow-x:auto;}
  code{background:${codeBg};padding:1px 5px;border-radius:5px;}
  blockquote{border-left:3px solid ${quoteBar};margin:10px 0;padding:2px 0 2px 12px;color:${muted};}
  hr{border:0;border-top:1px solid ${rule};margin:16px 0;}
  h1,h2,h3,h4,h5,h6{line-height:1.25;margin:18px 0 8px;font-weight:700;}
  h1{font-size:24px;}h2{font-size:21px;}h3{font-size:18px;}h4,h5,h6{font-size:16px;}
  p{margin:0 0 12px;}
  ul,ol{padding-left:22px;margin:0 0 12px;}
  li{margin:3px 0;}
  .__mailscroll{max-width:100%;overflow-x:auto;-webkit-overflow-scrolling:touch;}
  .__blockedimg{min-width:96px;min-height:60px;border:1px dashed ${quoteBar};border-radius:10px;
    background:${blockedBg} url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${blockedIcon}' stroke-width='1.6'><rect x='3' y='4' width='18' height='16' rx='2'/><circle cx='9' cy='10' r='1.6'/><path d='M3 17l5-5 4 4 3-3 6 6'/></svg>") center/26px no-repeat;}
  ${isDark ? 'img{background-color:#FFFFFF;border-radius:4px;}' : ''}
</style></head><body><div id="__mailroot">${processed}</div>
<script>
(function(){
  var PAD = ${pad};
  var DARK = ${isDark ? 'true' : 'false'};
  var root = document.getElementById('__mailroot');
  var scale = 1, lastPosted = 0, pending = false, fitPending = false;

  function send(payload){
    try { if (window.ReactNativeWebView) { window.ReactNativeWebView.postMessage(JSON.stringify(payload)); } } catch (e) {}
  }
  function applyScale(k){
    if (!root) { return; }
    if (k >= 1) {
      root.style.transform = 'none';
      root.style.width = 'auto';

      return;
    }
    root.style.transformOrigin = 'top left';
    root.style.transform = 'scale(' + k + ')';
    root.style.width = (100 / k) + '%';
  }
  /* Scale a fixed-width desktop e-mail down so it FITS instead of being clipped:
     unscale, read the natural width, then scale back to the viewport. */
  function refit(){
    if (!root) { return false; }
    var before = scale;
    applyScale(1);
    var avail = document.documentElement.clientWidth - PAD * 2;
    var natural = root.scrollWidth;
    var next = 1;
    if (avail > 0 && natural > avail + 2) {
      next = avail / natural;
      if (next < 0.3) { next = 0.3; }
    }
    scale = next;
    applyScale(next);

    return Math.abs(before - next) > 0.005;
  }
  function measure(){
    /* Measure the CONTENT only. documentElement/body.scrollHeight never drop
       below the viewport height, so once the native WebView grew by the +6px
       slack it would re-measure itself as taller, forever (endless scroll). */
    if (!root) { return Math.ceil(document.body ? document.body.offsetHeight : 0); }
    var boxed = root.getBoundingClientRect().height + PAD * 2;
    return Math.ceil(scale < 1 ? boxed : Math.max(boxed, root.scrollHeight + PAD * 2));
  }
  function post(reset){
    var h = measure();
    if (!h) { return; }
    if (!reset && h === lastPosted) { return; }
    lastPosted = h;
    send({type:'height', height:h, reset:!!reset});
  }
  function fitAndPost(){ post(refit()); }
  /* Observer-driven path: MEASURE ONLY. It must never write to the DOM, or the
     Mutation/Resize observers would re-trigger themselves forever. */
  function schedule(){
    if (pending) { return; }
    pending = true;
    setTimeout(function(){ pending = false; post(false); }, 16);
  }
  /* Event-driven path (finite: images, fonts, resize, the decaying interval):
     re-computes the fit-to-width scale as well. */
  function scheduleFit(){
    if (fitPending) { return; }
    fitPending = true;
    setTimeout(function(){ fitPending = false; fitAndPost(); }, 32);
  }
  window.__mailPost = scheduleFit;

  /* Wide DATA tables scroll horizontally on their own; nested layout tables are
     left untouched so newsletter skeletons keep their design. */
  function wrapWideTables(){
    var avail = document.documentElement.clientWidth - PAD * 2;
    var tables = root ? root.querySelectorAll('table') : [];
    for (var i = 0; i < tables.length; i++) {
      var t = tables[i];
      if (t.getAttribute('data-wrapped') || t.querySelector('table')) { continue; }
      if (t.scrollWidth > avail + 8) {
        t.setAttribute('data-wrapped', '1');
        var box = document.createElement('div');
        box.className = '__mailscroll';
        t.parentNode.insertBefore(box, t);
        box.appendChild(t);
      }
    }
  }
  /* Blocked images become an obvious placeholder tile; 1x1 tracking pixels just
     disappear instead of littering the mail with tiny grey dots. */
  function decorateBlocked(){
    var list = document.querySelectorAll('img[data-blocked-src]');
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      var w = parseInt(el.getAttribute('width') || '0', 10);
      var h = parseInt(el.getAttribute('height') || '0', 10);
      if ((w > 0 && w <= 3) || (h > 0 && h <= 3)) { el.style.display = 'none'; }
      else if (el.className.indexOf('__blockedimg') < 0) { el.className += ' __blockedimg'; }
    }
  }
  function bindImages(){
    var imgs = document.images || [];
    for (var i = 0; i < imgs.length; i++) {
      if (imgs[i].getAttribute('data-bound')) { continue; }
      imgs[i].setAttribute('data-bound', '1');
      imgs[i].addEventListener('load', scheduleFit);
      imgs[i].addEventListener('error', scheduleFit);
    }
  }
  /* ── dark-mode readability ───────────────────────────────────────────── */
  function luminance(color){
    var s = String(color || '');
    var open = s.indexOf('(');
    var close = s.indexOf(')');
    if (open < 0 || close < 0) { return -1; }
    var p = s.slice(open + 1, close).split(',');
    if (p.length < 3) { return -1; }
    if (p.length > 3 && parseFloat(p[3]) < 0.15) { return -1; }
    return (0.299 * parseFloat(p[0]) + 0.587 * parseFloat(p[1]) + 0.114 * parseFloat(p[2])) / 255;
  }
  function backdrop(el){
    var n = el;
    while (n && n !== document.documentElement) {
      var l = luminance(window.getComputedStyle(n).backgroundColor);
      if (l >= 0) { return l; }
      n = n.parentElement;
    }
    return DARK ? 0 : 1;
  }
  /* Repair unreadable pairs BOTH ways: dark ink on our navy surface, and light
     ink on the white card a newsletter brings with it. Images are never
     inverted — only sub-4px tracking pixels lose their white plate. */
  function harmonize(){
    if (!DARK || !root) { return; }
    var nodes = root.querySelectorAll('*');
    if (nodes.length > 3000) { return; }
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.nodeName === 'IMG') {
        var r = el.getBoundingClientRect();
        if (r.width <= 4 || r.height <= 4) { el.style.backgroundColor = 'transparent'; }
        continue;
      }
      if (el.nodeName === 'STYLE' || el.nodeName === 'SCRIPT' || !el.firstChild) { continue; }
      var ink = luminance(window.getComputedStyle(el).color);
      if (ink < 0) { continue; }
      var back = backdrop(el);
      if (back < 0.4 && ink < 0.5) { el.style.color = '#E2E8F0'; }
      else if (back > 0.65 && ink > 0.6) { el.style.color = '#0F172A'; }
    }
  }

  /* Restore every blocked asset WITHOUT reloading the document, so "show
     images" is instant and the reader keeps its scroll position. */
  window.__mailUnblock = function(){
    var i, el, list;
    list = document.querySelectorAll('[data-blocked-src]');
    for (i = 0; i < list.length; i++) {
      el = list[i];
      el.className = el.className.split(' __blockedimg').join('');
      el.style.display = '';
      el.setAttribute('src', el.getAttribute('data-blocked-src'));
      el.removeAttribute('data-blocked-src');
    }
    list = document.querySelectorAll('[data-blocked-srcset]');
    for (i = 0; i < list.length; i++) { el = list[i]; el.setAttribute('srcset', el.getAttribute('data-blocked-srcset')); el.removeAttribute('data-blocked-srcset'); }
    list = document.querySelectorAll('[data-blocked-poster]');
    for (i = 0; i < list.length; i++) { el = list[i]; el.setAttribute('poster', el.getAttribute('data-blocked-poster')); el.removeAttribute('data-blocked-poster'); }
    list = document.querySelectorAll('[data-blocked-background]');
    for (i = 0; i < list.length; i++) { el = list[i]; el.setAttribute('background', el.getAttribute('data-blocked-background')); el.removeAttribute('data-blocked-background'); }
    list = document.querySelectorAll('[style*="blocked-http"]');
    for (i = 0; i < list.length; i++) { el = list[i]; el.setAttribute('style', el.getAttribute('style').split('blocked-http').join('http')); }
    list = document.querySelectorAll('style');
    for (i = 0; i < list.length; i++) { list[i].textContent = list[i].textContent.split('blocked-http').join('http'); }
    bindImages();
    scheduleFit();
  };

  /* Every link tap is handed to React Native — this frame is height-capped and
     must never navigate away from the mail. */
  document.addEventListener('click', function(ev){
    var a = ev.target;
    while (a && a.nodeName !== 'A') { a = a.parentElement; }
    if (!a) { return; }
    var href = a.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#') { return; }
    ev.preventDefault();
    ev.stopPropagation();
    send({type:'link', url:(a.href || href)});
  }, true);

  function ready(){
    bindImages();
    decorateBlocked();
    wrapWideTables();
    harmonize();
    fitAndPost();
    send({type:'ready'});
  }
  document.addEventListener('DOMContentLoaded', ready);
  window.addEventListener('load', function(){ decorateBlocked(); wrapWideTables(); harmonize(); fitAndPost(); });
  window.addEventListener('resize', fitAndPost);
  try { if (document.fonts && document.fonts.ready) { document.fonts.ready.then(fitAndPost); } } catch (e) {}
  if (typeof ResizeObserver !== 'undefined') {
    try { new ResizeObserver(schedule).observe(root || document.documentElement); } catch (e) {}
  }
  if (typeof MutationObserver !== 'undefined') {
    try { new MutationObserver(schedule).observe(root || document.body, {childList:true, subtree:true, attributes:true}); } catch (e) {}
  }
  /* Decaying safety net (~12s) for WebViews without observers and for assets
     that settle very late — a mail must never stay frozen at half its height. */
  var ticks = 0;
  var iv = setInterval(function(){
    ticks += 1;
    if (ticks === 4 || ticks === 12 || ticks === 24) { wrapWideTables(); harmonize(); }
    fitAndPost();
    if (ticks > 48) { clearInterval(iv); }
  }, 250);
  ready();
}());
</script></body></html>`;
};

/**
 * Translate a mail key with an English fallback, so a primitive keeps reading
 * correctly even when a call site has not (yet) handed it the `t` function.
 * @param {Function} [t] i18n translator.
 * @param {string} key Translation key (always `mail_*`).
 * @param {string} fallback English fallback text.
 * @returns {string} The localized (or fallback) label.
 */
const tr = (t, key, fallback) => {
    if (typeof t !== 'function') { return fallback; }
    const value = t(key);

    return (!value || value === key) ? fallback : value;
};

/** Named HTML entities that actually occur in real e-mail bodies. */
const HTML_ENTITIES = {
    nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: '\'', hellip: '…',
    mdash: '—', ndash: '–', laquo: '«', raquo: '»', middot: '·',
    bull: '•', copy: '©', reg: '®', trade: '™', euro: '€',
    pound: '£', deg: '°', lsquo: '‘', rsquo: '’', ldquo: '“',
    rdquo: '”', szlig: 'ß', shy: '',
};

/**
 * Decode the HTML entities (named, decimal and hex) that would otherwise leak
 * into the plain-text reader as literal `&amp;` / `&#39;` noise.
 * @param {string} value Text that may contain entities.
 * @returns {string} Decoded text.
 */
const decodeEntities = (value) => String(value || '')
    .replace(/&#x([0-9a-f]+);/gi, (all, hex) => {
        const code = parseInt(hex, 16);

        return (code > 0 && code <= 0x10FFFF) ? String.fromCodePoint(code) : all;
    })
    .replace(/&#(\d+);/g, (all, dec) => {
        const code = parseInt(dec, 10);

        return (code > 0 && code <= 0x10FFFF) ? String.fromCodePoint(code) : all;
    })
    .replace(/&([a-z]+);/gi, (all, name) => {
        const hit = HTML_ENTITIES[String(name).toLowerCase()];

        return hit === undefined ? all : hit;
    });

/**
 * Turn e-mail HTML into readable plain text for the fallback renderer: script
 * and style CONTENT is dropped (never printed as text), block ends become
 * paragraph breaks, `<br>`/list items become single breaks and entities are
 * decoded — so a mail without HTML support still reads like a mail.
 * @param {string} html Raw e-mail HTML.
 * @returns {string} Paragraph-separated plain text.
 */
const htmlToPlain = (html) => decodeEntities(String(html || '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<(script|style|head|title)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\s*\/\s*(li|tr|dt|dd)\s*>/gi, '\n')
    .replace(/<\s*\/\s*(p|div|section|article|header|footer|h[1-6]|table|blockquote|ul|ol|pre)\s*>/gi, '\n\n')
    .replace(/<[^>]+>/g, ' '))
    .replace(/ /g, ' ')
    .replace(/[ \t]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

/** Splits a paragraph into text / URL / e-mail chunks (matches land on odd indexes). */
const LINK_SPLIT = /(https?:\/\/[^\s<>"')\]]+|www\.[^\s<>"')\]]+|[\w.+-]+@[\w-]+\.[\w.-]{2,})/i;

/**
 * Default link opener used when a caller supplies no `onLinkPress`: http(s)
 * goes to the in-app browser, `mailto:`/`tel:`/`sms:` to the OS. Never throws.
 * @param {string} url Target URL.
 * @returns {void}
 */
export const openMailLink = (url) => {
    const target = String(url || '').trim();
    if (!target) { return; }
    if (/^https?:/i.test(target)) {
        WebBrowser.openBrowserAsync(target).catch(() => {
            Linking.openURL(target).catch(() => {});
        });

        return;
    }
    if (/^(mailto|tel|sms):/i.test(target)) {
        Linking.openURL(target).catch(() => {});
    }
};

/**
 * Shared shimmer driver behind every skeleton in this kit — one looping value
 * per skeleton instead of one per bar.
 * @returns {Object} An animated opacity oscillating between 0.4 and 1.
 */
const useShimmer = () => {
    const shimmer = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        const loop = Animated.loop(
            Animated.sequence([
                Animated.timing(shimmer, {toValue: 1, duration: 850, easing: Easing.inOut(Easing.ease), useNativeDriver: true}),
                Animated.timing(shimmer, {toValue: 0, duration: 850, easing: Easing.inOut(Easing.ease), useNativeDriver: true}),
            ]),
        );
        loop.start();

        return () => loop.stop();
    }, [shimmer]);

    return shimmer.interpolate({inputRange: [0, 1], outputRange: [0.4, 1]});
};

/**
 * Body-shaped shimmer shown while the reader paints its first frame, so a mail
 * never appears as an empty box while it loads.
 * @param {{lines?:number}} props Optional line count (defaults to 6).
 * @returns {JSX.Element}
 */
export const MailBodySkeleton = ({lines = 6}) => {
    const m = useManagementTheme();
    const opacity = useShimmer();
    const widths = ['92%', '78%', '88%', '64%', '95%', '72%', '84%', '56%'];

    return (
        <View style={styles.bodySkeleton}>
            <Animated.View style={[styles.skelBar, {width: '46%', height: 15, backgroundColor: m.colors.surfaceAlt, opacity}]} />
            <View style={{height: 10}} />
            {Array.from({length: Math.max(1, lines)}).map((_, i) => (
                <Animated.View key={`sk-${i}`} style={[styles.skelBar, {width: widths[i % widths.length], backgroundColor: m.colors.surfaceAlt, opacity}]} />
            ))}
        </View>
    );
};

/**
 * Plain-text mail body: paragraph-aware, quote-aware and link-aware. Used when
 * a message carries no HTML (or the WebView could not render it), so the reader
 * still looks like a mail client instead of a wall of tags.
 * @param {{text?:string, onLinkPress?:(url:string)=>void, style?:any}} props Optional styling + link sink.
 * @returns {JSX.Element|null}
 */
export const MailPlainBody = ({text, onLinkPress, style, t}) => {
    const m = useManagementTheme();
    const open = useCallback((url) => {
        if (typeof onLinkPress === 'function') {
            onLinkPress(url);

            return;
        }
        openMailLink(url);
    }, [onLinkPress]);
    const paragraphs = useMemo(
        () => String(text || '').split(/\n{2,}/).map((p) => p.trim()).filter(Boolean),
        [text],
    );

    if (!paragraphs.length) {
        return (
            <View style={[styles.plainWrap, style]}>
                <Text style={[styles.plainPara, styles.plainEmpty, {color: m.colors.muted}]}>
                    {tr(t, 'mail_no_content', '(This message has no content.)')}
                </Text>
            </View>
        );
    }

    return (
        <View style={[styles.plainWrap, style]}>
            {paragraphs.map((para, index) => {
                const quoted = /^\s*>/.test(para) || /^\s*(-{2,}|_{2,})\s*(original|eredeti|forwarded|weitergeleitet)/i.test(para);
                const clean = quoted ? para.replace(/^[ \t]*>+[ \t]?/gm, '') : para;

                return (
                    <View
                        key={`para-${index}`}
                        style={quoted ? [styles.plainQuote, {borderLeftColor: m.colors.border}] : styles.plainBlock}
                    >
                        <Text selectable style={[styles.plainPara, {color: quoted ? m.colors.subText : m.colors.text}]}>
                            {clean.split(LINK_SPLIT).map((chunk, part) => {
                                if (!chunk) {
                                    return null;
                                }
                                if (part % 2 === 0) {
                                    return <Text key={`t-${part}`}>{chunk}</Text>;
                                }
                                const href = /^https?:/i.test(chunk)
                                    ? chunk
                                    : (/^www\./i.test(chunk) ? `https://${chunk}` : `mailto:${chunk}`);

                                return (
                                    <Text key={`l-${part}`} style={styles.plainLink} onPress={() => open(href)}>{chunk}</Text>
                                );
                            })}
                        </Text>
                    </View>
                );
            })}
        </View>
    );
};

/**
 * Premium e-mail body renderer — the reader's centrepiece.
 *
 * Renders the real HTML of a message in a sandboxed WebView that behaves like a
 * first-class mail client:
 *  - **Links** never navigate the frame: taps are intercepted (both in-page and
 *    natively) and handed to `onLinkPress` (default: in-app browser / OS).
 *  - **Height** is re-measured on load, on every image, via Resize/Mutation
 *    observers and a decaying timer, and only ever grows — so a late-loading
 *    image can never leave a mail rendered "only half".
 *  - **Perceived speed**: a shimmer overlay covers the frame until its first
 *    paint, then cross-fades out; the WebView underneath is never remounted.
 *  - **Remote content**: `blockRemote` neutralises every remote asset; allowing
 *    images is applied in place (no reload, no scroll jump).
 *  - **Fallback**: no HTML (or a hard WebView failure) renders {@link MailPlainBody}.
 *
 * @param {{html?:string, text?:string, blockRemote?:boolean,
 *          onLinkPress?:(url:string)=>void, onHeight?:(height:number)=>void,
 *          t?:Function}} props Body content and optional hooks.
 * @returns {JSX.Element}
 */
export const MailHtmlBody = ({html, text, blockRemote = false, onLinkPress, onHeight, t}) => {
    const m = useManagementTheme();
    const webRef = useRef(null);
    const maxRef = useRef(0);
    const builtRef = useRef({blocked: blockRemote});
    const skelFade = useRef(new Animated.Value(1)).current;
    const {height: windowHeight} = useWindowDimensions();
    const floor = useMemo(() => Math.max(200, Math.round(windowHeight * 0.32)), [windowHeight]);
    const [docVersion, setDocVersion] = useState(0);
    const [height, setHeight] = useState(floor);
    const [failed, setFailed] = useState(false);
    const [painted, setPainted] = useState(false);
    const [skeleton, setSkeleton] = useState(true);
    const [expanded, setExpanded] = useState(false);

    const hasHtml = !!String(html || '').trim();
    const plain = useMemo(() => String(text || '').trim() || htmlToPlain(html), [text, html]);
    const softCap = useMemo(() => Math.round(windowHeight * (expanded ? 40 : 6)), [windowHeight, expanded]);

    // `blockRemote` is deliberately NOT a dependency: switching it OFF is applied
    // in place by the effect below, so allowing images never reloads the mail.
    const doc = useMemo(() => {
        builtRef.current = {blocked: blockRemote};

        return buildHtmlDocument(html, {blockRemote, isDark: m.isDark});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [html, m.isDark, docVersion]);

    /* A different document = a fresh render: drop every per-message state. */
    useEffect(() => {
        maxRef.current = 0;
        setHeight(floor);
        setFailed(false);
        setPainted(false);
        setSkeleton(true);
        setExpanded(false);
        skelFade.setValue(1);
    }, [doc, floor, skelFade]);

    /* Safety net: a WebView that never reports back must not strand the skeleton. */
    useEffect(() => {
        if (painted) {
            return undefined;
        }
        const timer = globalThis.setTimeout(() => setPainted(true), 1400);

        return () => globalThis.clearTimeout(timer);
    }, [painted, doc]);

    /* Cross-fade the shimmer out, then unmount it. */
    useEffect(() => {
        if (!painted) {
            return undefined;
        }
        const anim = Animated.timing(skelFade, {toValue: 0, duration: 220, easing: Easing.out(Easing.quad), useNativeDriver: true});
        anim.start(({finished}) => {
            if (finished) {
                setSkeleton(false);
            }
        });

        return () => anim.stop();
    }, [painted, skelFade]);

    /* Allowing images is instant (in-place); re-blocking needs a clean document. */
    useEffect(() => {
        if (builtRef.current.blocked === blockRemote) {
            return;
        }
        if (!blockRemote) {
            builtRef.current.blocked = false;
            webRef.current?.injectJavaScript?.('window.__mailUnblock && window.__mailUnblock(); true;');

            return;
        }
        setDocVersion((v) => v + 1);
    }, [blockRemote]);

    useEffect(() => {
        onHeight?.(height);
    }, [height, onHeight]);

    const handleLink = useCallback((url) => {
        const target = String(url || '').trim();
        if (!target) {
            return;
        }
        if (typeof onLinkPress === 'function') {
            onLinkPress(target);

            return;
        }
        openMailLink(target);
    }, [onLinkPress]);

    /* Only the seeded document may load inside the frame; everything else is a
       link the user tapped and belongs to the OS / in-app browser. */
    const handleShouldStartLoad = useCallback((request) => {
        const url = String(request?.url || '');
        if (!url || url === 'about:blank' || /^(about|data|file|blob):/i.test(url)) {
            return true;
        }
        if (request?.isTopFrame === false) {
            return true;
        }
        if (/^(https?|mailto|tel|sms):/i.test(url)) {
            handleLink(url);
        }

        return false;
    }, [handleLink]);

    const handleMessage = useCallback((event) => {
        let payload = null;
        try {
            payload = JSON.parse(event?.nativeEvent?.data || '{}');
        } catch (_e) {
            return;
        }
        if (payload?.type === 'link') {
            handleLink(payload.url);

            return;
        }
        if (payload?.type === 'ready') {
            setPainted(true);

            return;
        }
        if (payload?.type === 'height' && payload.height) {
            const next = Math.max(80, Math.ceil(payload.height) + 6);
            maxRef.current = payload.reset ? next : Math.max(maxRef.current, next);
            setHeight(maxRef.current);
            setPainted(true);
        }
    }, [handleLink]);

    const handleLoadEnd = useCallback(() => {
        setPainted(true);
        webRef.current?.injectJavaScript?.('window.__mailPost && window.__mailPost(); true;');
    }, []);

    // No HTML at all, or a hard WebView failure → always show the message text.
    if (!hasHtml || failed) {
        return <MailPlainBody text={plain} onLinkPress={handleLink} t={t} />;
    }

    const capped = height > softCap;
    const display = Math.max(120, Math.min(height, softCap));

    return (
        <View style={styles.bodyWrap}>
            <View style={{height: display}}>
                <WebView
                    ref={webRef}
                    originWhitelist={['*']}
                    source={{html: doc}}
                    style={styles.bodyWeb}
                    scrollEnabled={capped}
                    nestedScrollEnabled={capped}
                    scalesPageToFit={false}
                    javaScriptEnabled
                    domStorageEnabled={false}
                    setSupportMultipleWindows={false}
                    overScrollMode="never"
                    showsVerticalScrollIndicator={false}
                    automaticallyAdjustContentInsets={false}
                    onShouldStartLoadWithRequest={handleShouldStartLoad}
                    onMessage={handleMessage}
                    onLoadEnd={handleLoadEnd}
                    onError={() => setFailed(true)}
                    onRenderProcessGone={() => setFailed(true)}
                    onContentProcessDidTerminate={() => setFailed(true)}
                    onHttpError={(event) => {
                        // Android reports SUB-RESOURCE failures here (a dead tracking
                        // pixel, a 404 webfont). Only a main-document failure may
                        // collapse the render to plain text.
                        const url = String(event?.nativeEvent?.url || '');
                        if (!url || url === 'about:blank') {
                            setFailed(true);
                        }
                    }}
                />
                {skeleton && (
                    <Animated.View
                        pointerEvents="none"
                        style={[StyleSheet.absoluteFill, {opacity: skelFade, backgroundColor: m.colors.surface}]}
                    >
                        <MailBodySkeleton />
                    </Animated.View>
                )}
            </View>
            {capped && !expanded && (
                <TouchableOpacity
                    onPress={() => setExpanded(true)}
                    activeOpacity={0.85}
                    accessibilityRole="button"
                    style={[styles.bodyMoreBtn, {backgroundColor: m.colors.chipBg, borderColor: m.colors.border}]}
                >
                    <Icon name="chevron-double-down" size={16} color={m.colors.primary} />
                    <Text style={[styles.bodyMoreText, {color: m.colors.primary}]} numberOfLines={1}>
                        {tr(t, 'mail_show_full_message', 'Show full message')}
                    </Text>
                </TouchableOpacity>
            )}
        </View>
    );
};

/**
 * Premium AI toolbox: a gradient sparkle trigger that opens a non-glassy
 * {@link PremiumSheet} of AI tool rows. Shared by the reader (MailThreadScreen)
 * and the composer (MailComposeScreen) so AI features are surfaced consistently.
 * @param {{title?:string, subtitle?:string, cancelText?:string,
 *          actions:Array<{key:string,label:string,sublabel?:string,icon?:string,gradient?:any,onPress:Function,busy?:boolean}>,
 *          iconColor?:string, style?:any}} props
 */
export const MailAiToolbox = ({title, subtitle, cancelText, actions = [], iconColor, style}) => {
    const m = useManagementTheme();
    const [open, setOpen] = useState(false);
    const busy = (Array.isArray(actions) ? actions : []).some((a) => a?.busy);

    return (
        <>
            <TouchableOpacity
                onPress={() => setOpen(true)}
                hitSlop={10}
                accessibilityRole="button"
                style={[styles.aiToolboxBtn, {backgroundColor: m.colors.surfaceAlt}, style]}
            >
                {busy
                    ? <ActivityIndicator size="small" color={iconColor || m.colors.primary} />
                    : <Icon name="auto-fix" size={20} color={iconColor || m.colors.primary} />}
            </TouchableOpacity>
            <PremiumSheet
                visible={open}
                onClose={() => setOpen(false)}
                title={title}
                subtitle={subtitle}
                cancelText={cancelText}
            >
                {(Array.isArray(actions) ? actions : []).map((a, i) => (
                    <SheetOption
                        key={a.key || i}
                        icon={a.icon || 'auto-fix'}
                        gradient={a.gradient || TILE_GRADIENTS.purple}
                        label={a.label}
                        sublabel={a.sublabel}
                        last={i === actions.length - 1}
                        onPress={() => {
                            setOpen(false);
                            a.onPress?.();
                        }}
                    />
                ))}
            </PremiumSheet>
        </>
    );
};

/* ───────────────────────── Attachment tile ──────────────────────── */

const ATTACH_ICONS = {
    pdf: 'file-pdf-box', image: 'file-image', word: 'file-word-box', excel: 'file-excel-box',
    powerpoint: 'file-powerpoint-box', zip: 'folder-zip-outline', audio: 'file-music-outline',
    video: 'file-video-outline', text: 'file-document-outline', calendar: 'calendar-month-outline',
    default: 'file-outline',
};

/**
 * Pick the MaterialCommunity glyph that matches an attachment's MIME type.
 * @param {string} [ct] The attachment `contentType`.
 * @returns {string} An icon name that always exists in the glyph map.
 */
const attachIcon = (ct = '') => {
    const type = String(ct).toLowerCase();
    if (type.includes('pdf')) { return ATTACH_ICONS.pdf; }
    if (type.includes('image')) { return ATTACH_ICONS.image; }
    if (type.includes('presentation') || type.includes('powerpoint')) { return ATTACH_ICONS.powerpoint; }
    if (type.includes('sheet') || type.includes('excel') || type.includes('csv')) { return ATTACH_ICONS.excel; }
    if (type.includes('word') || type.includes('document')) { return ATTACH_ICONS.word; }
    if (type.includes('zip') || type.includes('compress') || type.includes('tar')) { return ATTACH_ICONS.zip; }
    if (type.includes('audio')) { return ATTACH_ICONS.audio; }
    if (type.includes('video')) { return ATTACH_ICONS.video; }
    if (type.includes('calendar')) { return ATTACH_ICONS.calendar; }
    if (type.includes('text')) { return ATTACH_ICONS.text; }

    return ATTACH_ICONS.default;
};

/**
 * Short uppercase file-kind badge ("PDF", "XLSX", …) derived from the filename
 * first and the MIME type second.
 * @param {Object} [attachment] The attachment descriptor.
 * @returns {string} A 1-5 character badge, or '' when nothing is known.
 */
const attachKind = (attachment) => {
    const name = String(attachment?.fileName || attachment?.filename || '');
    const dot = name.lastIndexOf('.');
    if (dot > -1 && dot < name.length - 1) {
        return name.slice(dot + 1).toUpperCase().slice(0, 5);
    }
    const type = String(attachment?.contentType || '');
    const slash = type.lastIndexOf('/');

    return slash > -1 ? type.slice(slash + 1).toUpperCase().slice(0, 5) : '';
};

/**
 * Human-readable byte size.
 * @param {number} [bytes]
 * @returns {string} e.g. `1.4 MB`, or '' when unknown.
 */
const humanSize = (bytes) => {
    const n = Number(bytes || 0);
    if (!n) { return ''; }
    if (n < 1024) { return `${n} B`; }
    if (n < 1048576) { return `${(n / 1024).toFixed(0)} KB`; }

    return `${(n / 1048576).toFixed(1)} MB`;
};

/**
 * Attachment row: file-type icon, name, size + kind badge and a clear tap
 * affordance (download in the reader, remove in the composer).
 * @param {{attachment:Object, onPress?:()=>void, busy?:boolean, onRemove?:()=>void, t?:Function}} props
 * @returns {JSX.Element}
 */
export const AttachmentTile = ({attachment, onPress, busy, onRemove, t}) => {
    const m = useManagementTheme();
    const label = attachment?.fileName || attachment?.filename || tr(t, 'mail_attachment', 'Attachment');
    const size = humanSize(attachment?.size);
    const kind = attachKind(attachment);

    return (
        <TouchableOpacity
            onPress={onPress}
            activeOpacity={0.8}
            disabled={!onPress}
            accessibilityRole="button"
            accessibilityLabel={label}
            style={[styles.attachTile, {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.borderSoft}]}
        >
            <View style={[styles.attachIcon, {backgroundColor: m.colors.chipBg}]}>
                <Icon name={attachIcon(attachment?.contentType)} size={20} color={m.colors.primary} />
            </View>
            <View style={{flex: 1, minWidth: 0}}>
                <Text style={[styles.attachName, {color: m.colors.heading}]} numberOfLines={1}>{label}</Text>
                <View style={styles.attachMetaRow}>
                    {!!kind && <Text style={[styles.attachKind, {color: m.colors.muted}]}>{kind}</Text>}
                    {!!size && <Text style={[styles.attachSize, {color: m.colors.subText}]}>{size}</Text>}
                </View>
            </View>
            {busy ? (
                <View style={styles.attachAction}><ActivityIndicator color={m.colors.primary} /></View>
            ) : onRemove ? (
                <TouchableOpacity onPress={onRemove} hitSlop={10} style={[styles.attachAction, {backgroundColor: m.colors.chipBg}]} accessibilityRole="button">
                    <Icon name="close" size={17} color={m.colors.muted} />
                </TouchableOpacity>
            ) : (
                <View style={[styles.attachAction, {backgroundColor: m.colors.chipBg}]}>
                    <Icon name="tray-arrow-down" size={18} color={m.colors.primary} />
                </View>
            )}
        </TouchableOpacity>
    );
};

/* ───────────────────────── Pills / selectors ────────────────────── */

/**
 * Horizontal folder / label pill: solid orange with white text when active, a
 * white pill with a hairline border otherwise (both share one height).
 * @param {{label:string, icon?:string, count?:number, active?:boolean, color?:string, onPress?:()=>void}} props
 * @returns {JSX.Element}
 */
export const FolderPill = ({label, icon, count, active, color, onPress}) => {
    const m = useManagementTheme();
    const content = (
        <>
            {!!icon && <Icon name={icon} size={14} color={active ? '#FFFFFF' : (color || m.colors.subText)} style={{marginRight: 6}} />}
            {!!color && !icon && <View style={[styles.folderDot, {backgroundColor: color}]} />}
            <Text style={[styles.folderPillText, {color: active ? '#FFFFFF' : m.colors.heading}]} numberOfLines={1}>{label}</Text>
            {count > 0 && (
                <View style={[styles.folderCount, {backgroundColor: active ? 'rgba(255,255,255,0.26)' : m.colors.chipBg}]}>
                    <Text style={[styles.folderCountText, {color: active ? '#FFFFFF' : m.colors.primary}]}>{count}</Text>
                </View>
            )}
        </>
    );

    // Active = solid brand pill; inactive = flat surface + hairline border.
    // Both branches share styles.folderPill so every pill keeps the same height.
    if (active) {
        return (
            <TouchableOpacity onPress={onPress} activeOpacity={0.85} style={[styles.folderPill, {backgroundColor: m.colors.primary, borderColor: m.colors.primary}]}>
                {content}
            </TouchableOpacity>
        );
    }
    return (
        <TouchableOpacity onPress={onPress} activeOpacity={0.8} style={[styles.folderPill, {backgroundColor: m.colors.surface, borderColor: m.colors.border}]}>
            {content}
        </TouchableOpacity>
    );
};

/** Feather-compatible icon per standard folder key (used inside {@link SheetOption}, which
 *  renders via `react-native-vector-icons/Feather` — the app's MaterialCommunityIcons folder
 *  glyphs used elsewhere don't apply here). Custom folders fall back to a plain 'folder'. */
const FOLDER_SHEET_ICONS = {
    INBOX: 'inbox', Sent: 'send', Drafts: 'edit-3', Archive: 'archive', Spam: 'alert-octagon', Trash: 'trash-2',
};

/**
 * Premium bottom sheet for moving selected message(s) into another mailbox
 * folder — lists the caller-resolved folders (standard + custom) as tappable
 * {@link SheetOption} rows with a folder-tinted icon tile.
 * @param {{visible:boolean, onClose:()=>void, title?:string, subtitle?:string,
 *          folders:Array<{key:string,label:string,color?:string}>,
 *          onSelect:(key:string)=>void}} props
 * @returns {JSX.Element}
 */
export const MailFolderPickerSheet = ({visible, onClose, title, subtitle, folders, onSelect}) => {
    const list = Array.isArray(folders) ? folders : [];

    return (
        <PremiumSheet visible={visible} onClose={onClose} title={title} subtitle={subtitle}>
            {list.map((f, i) => (
                <SheetOption
                    key={f.key}
                    icon={FOLDER_SHEET_ICONS[f.key] || 'folder'}
                    gradient={f.color ? [f.color, f.color] : TILE_GRADIENTS.blue}
                    label={f.label}
                    last={i === list.length - 1}
                    onPress={() => onSelect(f.key)}
                />
            ))}
        </PremiumSheet>
    );
};

/**
 * Floating orange compose button (brand gradient, white glyph, soft orange glow).
 * @param {{onPress?:()=>void, icon?:string, label?:string}} props
 * @returns {JSX.Element}
 */
export const ComposeFab = ({onPress, icon = 'pencil', label}) => {
    const m = useManagementTheme();
    return (
        <TouchableOpacity onPress={onPress} activeOpacity={0.85} style={styles.fabWrap}>
            <LinearGradient colors={m.gradients.primary} start={{x: 0, y: 0}} end={{x: 1, y: 1}} style={[styles.fab, styles.fabGlow]}>
                <Icon name={icon} size={22} color="#FFFFFF" />
                {!!label && <Text style={styles.fabText}>{label}</Text>}
            </LinearGradient>
        </TouchableOpacity>
    );
};

/* ───────────────────────── Remote-content banner ────────────────── */

/**
 * "Remote images blocked" banner with two explicit, premium actions:
 * show once, or always trust this sender.
 * @param {{onShow:()=>void, onAlwaysAllow?:()=>void, t?:Function}} props
 * @returns {JSX.Element}
 */
export const RemoteContentBanner = ({onShow, onAlwaysAllow, t}) => {
    const m = useManagementTheme();

    return (
        <View style={[styles.remoteBanner, {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.border}]}>
            <View style={[styles.remoteIcon, {backgroundColor: m.colors.chipBg}]}>
                <Icon name="image-off-outline" size={17} color={m.colors.warning} />
            </View>
            <View style={styles.remoteTextWrap}>
                <Text style={[styles.remoteTitle, {color: m.colors.heading}]} numberOfLines={1}>
                    {tr(t, 'mail_remote_content', 'Remote content')}
                </Text>
                <Text style={[styles.remoteText, {color: m.colors.subText}]} numberOfLines={2}>
                    {tr(t, 'mail_remote_blocked', 'Images in this message were blocked to protect your privacy.')}
                </Text>
            </View>
            <View style={styles.remoteActions}>
                <TouchableOpacity
                    onPress={onShow}
                    activeOpacity={0.85}
                    accessibilityRole="button"
                    style={[styles.remoteBtn, {backgroundColor: m.colors.primary, borderColor: m.colors.primary}]}
                >
                    <Text style={[styles.remoteBtnText, {color: '#FFFFFF'}]} numberOfLines={1}>
                        {tr(t, 'mail_show_images', 'Show images')}
                    </Text>
                </TouchableOpacity>
                {!!onAlwaysAllow && (
                    <TouchableOpacity
                        onPress={onAlwaysAllow}
                        activeOpacity={0.85}
                        accessibilityRole="button"
                        style={[styles.remoteBtn, {backgroundColor: 'transparent', borderColor: m.colors.border}]}
                    >
                        <Text style={[styles.remoteBtnText, {color: m.colors.primary}]} numberOfLines={1}>
                            {tr(t, 'mail_always_allow', 'Always allow')}
                        </Text>
                    </TouchableOpacity>
                )}
            </View>
        </View>
    );
};

/* ───────────────────────── Quick-filter segmented bar ───────────── */

/**
 * Quick filter (All / Unread / Flagged / Attachments) rendered as a clean
 * horizontal scroll row of auto-width pill chips (uniform ~31px height, 8px
 * gaps, count badge inside the chip). Scrollable so long localized labels can
 * never overflow into a neighbouring chip.
 * @param {{value:string, options:Array<{key:string,label:string,icon?:string,count?:number}>, onChange:(key:string)=>void}} props
 */
export const QuickFilterBar = ({value, options, onChange}) => {
    const m = useManagementTheme();
    return (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.segRow}>
            {(Array.isArray(options) ? options : []).map((opt) => {
                const active = value === opt.key;
                return (
                    <TouchableOpacity
                        key={opt.key}
                        activeOpacity={0.85}
                        onPress={() => onChange(opt.key)}
                        style={[styles.segChip, {
                            backgroundColor: active ? m.colors.primary : m.colors.surface,
                            borderColor: active ? m.colors.primary : m.colors.border,
                        }]}
                    >
                        {!!opt.icon && <Icon name={opt.icon} size={13} color={active ? '#FFFFFF' : m.colors.muted} style={{marginRight: 5}} />}
                        <Text style={[styles.segText, {color: active ? '#FFFFFF' : m.colors.subText}]} numberOfLines={1}>{opt.label}</Text>
                        {opt.count > 0 && (
                            <View style={[styles.segCount, {backgroundColor: active ? 'rgba(255,255,255,0.26)' : m.colors.surfaceAlt}]}>
                                <Text style={[styles.segCountText, {color: active ? '#FFFFFF' : m.colors.subText}]}>{opt.count > 99 ? '99+' : opt.count}</Text>
                            </View>
                        )}
                    </TouchableOpacity>
                );
            })}
        </ScrollView>
    );
};

/* ───────────────────────── Section (date) header ────────────────── */

export const MailSectionHeader = ({label}) => {
    const m = useManagementTheme();
    return (
        <View style={styles.sectionHeader}>
            <Text style={[styles.sectionHeaderText, {color: m.colors.muted}]}>{label}</Text>
            <View style={[styles.sectionLine, {backgroundColor: m.colors.borderSoft}]} />
        </View>
    );
};

/* ───────────────────────── AI assist toolbar ────────────────────── */

/**
 * Horizontal AI action pills (summarize / translate / reply, etc.).
 * @param {{actions:Array<{key:string,label:string,icon?:string,onPress:()=>void,busy?:boolean,primary?:boolean}>, title?:string}} props
 */
export const AiAssistBar = ({actions = [], title}) => {
    const m = useManagementTheme();
    if (!actions.length) return null;
    return (
        <View style={[styles.aiBar, {backgroundColor: m.isDark ? 'rgba(124,58,237,0.10)' : '#F5F3FF', borderColor: m.isDark ? 'rgba(167,139,250,0.30)' : '#E4DCFB'}]}>
            <View style={styles.aiBadge}>
                <LinearGradient colors={m.gradients.violet} start={{x: 0, y: 0}} end={{x: 1, y: 1}} style={styles.aiBadgeInner}>
                    <Icon name="auto-fix" size={13} color="#FFFFFF" />
                </LinearGradient>
                {!!title && <Text style={[styles.aiBarTitle, {color: m.colors.violet}]}>{title}</Text>}
            </View>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{gap: 8, paddingRight: 6}}>
                {actions.map((a) => (
                    <TouchableOpacity
                        key={a.key}
                        activeOpacity={0.85}
                        onPress={a.onPress}
                        disabled={a.busy}
                        style={[styles.aiChip, {backgroundColor: a.primary ? m.colors.violet : m.colors.surface, borderColor: a.primary ? m.colors.violet : m.colors.border}]}
                    >
                        {a.busy ? <ActivityIndicator size="small" color={a.primary ? '#FFFFFF' : m.colors.violet} />
                            : !!a.icon && <Icon name={a.icon} size={14} color={a.primary ? '#FFFFFF' : m.colors.violet} style={{marginRight: 5}} />}
                        <Text style={[styles.aiChipText, {color: a.primary ? '#FFFFFF' : m.colors.heading}]}>{a.label}</Text>
                    </TouchableOpacity>
                ))}
            </ScrollView>
        </View>
    );
};

/* ───────────────────────── Smart reply chips ────────────────────── */

/** Suggested one-tap replies row. @param {{suggestions:string[], onPick:(text:string)=>void}} props */
export const SmartReplyChips = ({suggestions = [], onPick}) => {
    const m = useManagementTheme();
    if (!suggestions.length) return null;
    return (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.smartWrap}>
            {suggestions.map((s, i) => (
                <TouchableOpacity key={`${s}-${i}`} activeOpacity={0.85} onPress={() => onPick(s, i)} style={[styles.smartChip, {backgroundColor: m.colors.surface, borderColor: m.colors.primary}]}>
                    <Icon name="reply" size={14} color={m.colors.primary} style={{marginRight: 5}} />
                    <Text style={[styles.smartChipText, {color: m.colors.heading}]} numberOfLines={1}>{s}</Text>
                </TouchableOpacity>
            ))}
        </ScrollView>
    );
};

/* ───────────────────────── Premium empty state ──────────────────── */

/**
 * Premium empty state for a folder, a search or the reader.
 * @param {{icon?:string, title?:string, text?:string, tone?:string,
 *          actionLabel?:string, actionIcon?:string, onAction?:()=>void}} props
 *        Everything is optional; the action pill only renders when both
 *        `actionLabel` and `onAction` are supplied.
 * @returns {JSX.Element}
 */
export const MailEmpty = ({icon = 'email-check-outline', title, text, tone = 'primary', actionLabel, actionIcon = 'refresh', onAction}) => {
    const m = useManagementTheme();

    return (
        <View style={styles.emptyWrap}>
            <View style={[styles.emptyIcon, {backgroundColor: m.toneTint(tone)}]}>
                <Icon name={icon} size={34} color={m.toneColor(tone)} />
            </View>
            {!!title && <Text style={[styles.emptyTitle, {color: m.colors.heading}]}>{title}</Text>}
            {!!text && <Text style={[styles.emptyText, {color: m.colors.subText}]}>{text}</Text>}
            {!!(actionLabel && onAction) && (
                <TouchableOpacity
                    onPress={onAction}
                    activeOpacity={0.85}
                    accessibilityRole="button"
                    style={[styles.emptyAction, {backgroundColor: m.colors.chipBg, borderColor: m.colors.border}]}
                >
                    <Icon name={actionIcon} size={16} color={m.colors.primary} />
                    <Text style={[styles.emptyActionText, {color: m.colors.primary}]} numberOfLines={1}>{actionLabel}</Text>
                </TouchableOpacity>
            )}
        </View>
    );
};

/* ───────────────────────── Home header primitives ───────────────── */

/**
 * Active mailbox chip (avatar + name + address) with a dropdown affordance —
 * opens the account switcher / mail settings. Mirrors the design's top-left
 * "Personal Account" chip.
 * @param {{name?:string, email?:string, who?:object, onPress?:()=>void}} props
 */
export const MailAccountChip = ({name, email, who, onPress}) => {
    const m = useManagementTheme();

    return (
        <TouchableOpacity onPress={onPress} activeOpacity={0.8} style={styles.acctChip} disabled={!onPress}>
            <MailAvatar who={who || {name, email}} size={40} />
            <View style={styles.acctText}>
                <Text style={[styles.acctName, {color: m.colors.heading}]} numberOfLines={1}>{name || email || ''}</Text>
                {!!email && <Text style={[styles.acctEmail, {color: m.colors.subText}]} numberOfLines={1}>{email}</Text>}
            </View>
            {!!onPress && <Icon name="chevron-down" size={18} color={m.colors.muted} />}
        </TouchableOpacity>
    );
};

/**
 * Horizontally scrolling account switcher under the large Home title: an "All inboxes" disc first,
 * then one round avatar per connected account (initials), a ring on the active one and a small
 * orange dot when it has unread mail (`unread` flag per account / for "all").
 * @param {{accounts:Array<{id:string,name?:string,email?:string,unread?:boolean}>, activeId:(string|null),
 *          allLabel:string, allUnread?:boolean, onSelect:(id:(string|null))=>void}} props `activeId` null = all inboxes.
 */
export const MailAccountStrip = ({accounts, activeId, allLabel, allUnread, onSelect}) => {
    const m = useManagementTheme();
    const list = Array.isArray(accounts) ? accounts : [];
    const dot = (on) => (on ? <View style={[styles.stripDot, {backgroundColor: m.colors.primary, borderColor: m.colors.bg}]} /> : null);
    const label = (text, active) => (
        <Text style={[styles.stripLabel, {color: active ? m.colors.heading : m.colors.subText}]} numberOfLines={1}>{text}</Text>
    );

    return (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.stripRow}>
            <TouchableOpacity
                activeOpacity={0.8}
                onPress={() => onSelect(null)}
                style={styles.stripItem}
                accessibilityRole="button"
                accessibilityLabel={allLabel}
            >
                <View style={[styles.stripRing, {borderColor: activeId === null ? m.colors.primary : 'transparent'}]}>
                    <View style={[styles.stripDisc, {backgroundColor: activeId === null ? m.colors.primary : m.colors.surfaceAlt, borderColor: m.colors.border}]}>
                        <Icon name="inbox-multiple-outline" size={22} color={activeId === null ? '#FFFFFF' : m.colors.heading} />
                    </View>
                </View>
                {dot(allUnread)}
                {label(allLabel, activeId === null)}
            </TouchableOpacity>
            {list.map((acc) => {
                const active = activeId === acc.id;
                const text = acc.name || acc.email || '';

                return (
                    <TouchableOpacity
                        key={acc.id}
                        activeOpacity={0.8}
                        onPress={() => onSelect(acc.id)}
                        style={styles.stripItem}
                        accessibilityRole="button"
                        accessibilityLabel={text}
                    >
                        <View style={[styles.stripRing, {borderColor: active ? m.colors.primary : 'transparent'}]}>
                            <MailAvatar who={{name: acc.name, email: acc.email}} size={46} />
                        </View>
                        {dot(acc.unread)}
                        {label(text, active)}
                    </TouchableOpacity>
                );
            })}
        </ScrollView>
    );
};

/**
 * Compact "person + name" assignee line (and a done check) for shared-mailbox rows.
 * @param {{name?:string, done?:boolean}} props
 */
export const MailAssigneeLine = ({name, done}) => {
    const m = useManagementTheme();

    if (!name && !done) {
        return null;
    }

    return (
        <View style={styles.assigneeLine}>
            {!!name && (
                <>
                    <Icon name="account-outline" size={13} color={m.colors.muted} />
                    <Text style={[styles.assigneeText, {color: m.colors.subText}]} numberOfLines={1}>{name}</Text>
                </>
            )}
            {!!done && <Icon name="check-circle" size={15} color={m.colors.success} style={{marginLeft: 'auto'}} />}
        </View>
    );
};

/** Prominent gradient "Write email" pill (design's top-right CTA). */
export const MailWriteButton = ({label, onPress}) => {
    const m = useManagementTheme();

    return (
        <TouchableOpacity onPress={onPress} activeOpacity={0.85} accessibilityRole="button" accessibilityLabel={label}>
            <LinearGradient colors={m.gradients.primary} start={{x: 0, y: 0}} end={{x: 1, y: 1}} style={[styles.writeBtn, m.shadow(2)]}>
                <Icon name="pencil-plus-outline" size={16} color="#FFFFFF" />
                <Text style={styles.writeBtnText} numberOfLines={1}>{label}</Text>
            </LinearGradient>
        </TouchableOpacity>
    );
};

/**
 * Compact home stat card (design's "Need replies / Meetings / Drafts" tiles).
 * @param {{icon:string, value:(number|string), label:string, tone?:string, active?:boolean, onPress?:()=>void}} props
 */
export const MailStatCard = ({icon, value, label, tone = 'info', active, onPress}) => {
    const m = useManagementTheme();
    const Wrap = onPress ? TouchableOpacity : View;

    return (
        <Wrap
            onPress={onPress}
            activeOpacity={0.85}
            style={[styles.statCard, {backgroundColor: active ? m.colors.chipBg : m.colors.surface, borderColor: active ? m.colors.primary : m.colors.border}, m.shadow(1)]}
        >
            <View style={styles.statTop}>
                <View style={[styles.statIcon, {backgroundColor: m.toneTint(tone)}]}>
                    <Icon name={icon} size={15} color={m.toneColor(tone)} />
                </View>
                <Text style={[styles.statValue, {color: m.colors.heading}]} numberOfLines={1}>{value}</Text>
            </View>
            <Text style={[styles.statLabel, {color: m.colors.subText}]} numberOfLines={1}>{label}</Text>
        </Wrap>
    );
};

/**
 * Tappable search bar with an optional folders button + AI sparkle (design's
 * "Search email" field). Tapping the field opens the dedicated search screen.
 * @param {{placeholder:string, onPress:()=>void, onAiPress?:()=>void, onFolders?:()=>void}} props
 */
export const MailSearchField = ({placeholder, onPress, onAiPress, onFolders}) => {
    const m = useManagementTheme();

    return (
        <View style={[styles.searchField, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}>
            {!!onFolders && (
                <TouchableOpacity onPress={onFolders} hitSlop={8} style={styles.searchFolderBtn} accessibilityRole="button">
                    <Icon name="folder-outline" size={19} color={m.colors.subText} />
                </TouchableOpacity>
            )}
            <TouchableOpacity onPress={onPress} activeOpacity={0.7} style={styles.searchTap} accessibilityRole="search">
                <Icon name="magnify" size={20} color={m.colors.muted} />
                <Text style={[styles.searchPlaceholder, {color: m.colors.placeholder}]} numberOfLines={1}>{placeholder}</Text>
            </TouchableOpacity>
            {!!onAiPress && (
                <TouchableOpacity onPress={onAiPress} hitSlop={8} accessibilityRole="button">
                    <LinearGradient colors={m.gradients.violet} start={{x: 0, y: 0}} end={{x: 1, y: 1}} style={styles.searchAiInner}>
                        <Icon name="auto-fix" size={15} color="#FFFFFF" />
                    </LinearGradient>
                </TouchableOpacity>
            )}
        </View>
    );
};

/**
 * Rounded category/filter chips row with counts (design's "Primary / Company /
 * Promotions" chips). Horizontally scrollable; the active chip fills orange.
 * @param {{value:string, options:Array<{key:string,label:string,icon?:string,count?:number}>, onChange:(key:string)=>void}} props
 */
export const MailFilterChips = ({value, options, onChange}) => {
    const m = useManagementTheme();

    return (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.fchipRow}>
            {(Array.isArray(options) ? options : []).map((opt) => {
                const active = value === opt.key;

                return (
                    <TouchableOpacity
                        key={opt.key}
                        onPress={() => onChange(opt.key)}
                        activeOpacity={0.85}
                        style={[styles.fchip, {backgroundColor: active ? m.colors.primary : m.colors.surface, borderColor: active ? m.colors.primary : m.colors.border}]}
                    >
                        {!!opt.icon && <Icon name={opt.icon} size={14} color={active ? '#FFFFFF' : m.colors.subText} style={{marginRight: 6}} />}
                        <Text style={[styles.fchipText, {color: active ? '#FFFFFF' : m.colors.heading}]} numberOfLines={1}>{opt.label}</Text>
                        {opt.count > 0 && (
                            <View style={[styles.fchipCount, {backgroundColor: active ? 'rgba(255,255,255,0.26)' : m.colors.chipBg}]}>
                                <Text style={[styles.fchipCountText, {color: active ? '#FFFFFF' : m.colors.primary}]}>{opt.count > 99 ? '99+' : opt.count}</Text>
                            </View>
                        )}
                    </TouchableOpacity>
                );
            })}
        </ScrollView>
    );
};

/* ───────────────────────── Loading skeleton ─────────────────────── */

/**
 * List-shaped shimmer used while a folder loads. Mirrors the real
 * {@link MailListItem} anatomy (lead gutter + avatar + sender/time + subject +
 * preview + inset divider) and shares {@link useShimmer} with
 * {@link MailBodySkeleton} so every loading surface pulses in step.
 * @param {{count?:number}} props Optional row count (defaults to 8).
 * @returns {JSX.Element}
 */
export const MailListSkeleton = ({count = 8}) => {
    const m = useManagementTheme();
    const opacity = useShimmer();

    return (
        <View style={{paddingHorizontal: 14}}>
            {Array.from({length: Math.max(1, count)}).map((_, i) => (
                <View key={i} style={[styles.item, {backgroundColor: m.colors.surface, borderColor: m.colors.border}]}>
                    <View style={styles.itemLead} />
                    <Animated.View style={[styles.skelAvatar, {backgroundColor: m.colors.border, opacity}]} />
                    <View style={styles.itemMain}>
                        <View style={styles.skelTopRow}>
                            <Animated.View style={[styles.skelLine, {width: '46%', backgroundColor: m.colors.border, opacity}]} />
                            <Animated.View style={[styles.skelLine, {width: 32, backgroundColor: m.colors.border, opacity}]} />
                        </View>
                        <Animated.View style={[styles.skelLine, {width: '72%', backgroundColor: m.colors.border, opacity}]} />
                        <Animated.View style={[styles.skelLine, {width: '90%', marginBottom: 0, backgroundColor: m.colors.border, opacity}]} />
                    </View>
                </View>
            ))}
        </View>
    );
};

const styles = StyleSheet.create({
    avatar: {alignItems: 'center', justifyContent: 'center'},
    avatarText: {fontWeight: '800'},
    avatarRing: {alignItems: 'center', justifyContent: 'center', borderWidth: 1.5},

    bodyWrap: {width: '100%'},
    bodyWeb: {flex: 1, backgroundColor: 'transparent'},
    bodySkeleton: {paddingHorizontal: 14, paddingTop: 16},
    bodyMoreBtn: {flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, alignSelf: 'center', borderRadius: 999, borderWidth: 1, paddingHorizontal: 16, paddingVertical: 9, marginTop: 10},
    bodyMoreText: {fontSize: 13, fontWeight: '800'},

    plainWrap: {paddingHorizontal: 14, paddingVertical: 12},
    plainBlock: {marginBottom: 10},
    plainQuote: {borderLeftWidth: 3, paddingLeft: 10, marginBottom: 10},
    plainPara: {fontSize: 15.5, lineHeight: 23},
    plainEmpty: {fontStyle: 'italic'},
    plainLink: {color: '#F97316', fontWeight: '700', textDecorationLine: 'underline'},
    aiToolboxBtn: {width: 38, height: 38, borderRadius: 12, alignItems: 'center', justifyContent: 'center'},

    labelChip: {flexDirection: 'row', alignItems: 'center', borderRadius: 999, borderWidth: 1, paddingHorizontal: 8, paddingVertical: 3, marginRight: 6},
    labelChipSm: {paddingHorizontal: 7, paddingVertical: 2},
    labelDot: {width: 6, height: 6, borderRadius: 3, marginRight: 5},
    labelChipText: {fontSize: 11, fontWeight: '800'},

    item: {flexDirection: 'row', alignItems: 'center', paddingVertical: 13, paddingLeft: 4, paddingRight: 12, borderRadius: 22, borderWidth: 1, marginVertical: 4},
    colorStripe: {position: 'absolute', left: 0, top: 14, bottom: 14, width: 4, borderRadius: 2},
    itemLead: {width: 16, alignItems: 'center'},
    unreadDot: {width: 8, height: 8, borderRadius: 4},
    itemMain: {flex: 1, marginLeft: 10},
    itemTopRow: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'},
    itemTopRight: {flexDirection: 'row', alignItems: 'center', gap: 4},
    readPill: {flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 7, paddingVertical: 2, borderRadius: 999, borderWidth: 1, marginRight: 6, maxWidth: 220},
    readPillText: {fontSize: 10.5, fontWeight: '800', flexShrink: 1},
    itemBottomRow: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 1},
    itemFrom: {flex: 1, fontSize: 15.5, paddingRight: 8, letterSpacing: -0.2},
    itemTime: {fontSize: 12, fontWeight: '700'},
    itemSubject: {flex: 1, fontSize: 14, marginTop: 2},
    itemPreview: {flex: 1, fontSize: 13, lineHeight: 18},
    itemLabels: {flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', rowGap: 4, marginTop: 5},
    itemStarBtn: {width: 22, height: 22, alignItems: 'center', justifyContent: 'center'},
    statusDot: {width: 8, height: 8, borderRadius: 4, marginLeft: 6},

    segRow: {flexDirection: 'row', alignItems: 'center', gap: 8, paddingLeft: 14, paddingRight: 24},
    segChip: {flexDirection: 'row', alignItems: 'center', height: 31, borderRadius: 999, borderWidth: 1, paddingHorizontal: 11},
    segText: {fontSize: 12.5, fontWeight: '700'},
    segCount: {marginLeft: 6, borderRadius: 999, paddingHorizontal: 5, minWidth: 17, alignItems: 'center'},
    segCountText: {fontSize: 10.5, fontWeight: '900'},

    sectionHeader: {flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 14, marginBottom: 4, paddingHorizontal: 6},
    sectionHeaderText: {fontSize: 11, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.8},
    sectionLine: {flex: 1, height: StyleSheet.hairlineWidth, borderRadius: 1},

    aiBar: {borderRadius: 16, borderWidth: 1, paddingVertical: 10, paddingLeft: 12, paddingRight: 6, marginBottom: 10},
    aiBadge: {flexDirection: 'row', alignItems: 'center', marginBottom: 8, gap: 7},
    aiBadgeInner: {width: 22, height: 22, borderRadius: 7, alignItems: 'center', justifyContent: 'center'},
    aiBarTitle: {fontSize: 12.5, fontWeight: '900', textTransform: 'uppercase', letterSpacing: 0.4},
    aiChip: {flexDirection: 'row', alignItems: 'center', borderRadius: 999, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 7},
    aiChipText: {fontSize: 12.5, fontWeight: '800'},

    smartWrap: {gap: 8, paddingVertical: 4, paddingHorizontal: 2},
    smartChip: {flexDirection: 'row', alignItems: 'center', borderRadius: 999, borderWidth: 1.5, paddingHorizontal: 13, paddingVertical: 8, maxWidth: 230},
    smartChipText: {fontSize: 13, fontWeight: '700'},

    emptyWrap: {alignItems: 'center', justifyContent: 'center', paddingVertical: 54, paddingHorizontal: 32},
    emptyIcon: {width: 84, height: 84, borderRadius: 42, alignItems: 'center', justifyContent: 'center', marginBottom: 16},
    emptyTitle: {fontSize: 17, fontWeight: '900', textAlign: 'center'},
    emptyText: {fontSize: 13.5, fontWeight: '600', textAlign: 'center', marginTop: 6, lineHeight: 19},
    emptyAction: {flexDirection: 'row', alignItems: 'center', gap: 6, borderRadius: 999, borderWidth: 1, paddingHorizontal: 16, paddingVertical: 9, marginTop: 14},
    emptyActionText: {fontSize: 13, fontWeight: '800'},

    swipeWrap: {justifyContent: 'center'},
    swipeAction: {width: 82, alignItems: 'center', justifyContent: 'center', borderRadius: 22, height: '82%'},

    recipientWrap: {paddingVertical: 10, borderBottomWidth: 1, position: 'relative', zIndex: 20},
    // While the autocomplete is open the field must out-rank every sibling row
    // (subject, body, action bar) on BOTH platforms: zIndex for iOS/web,
    // elevation for Android — otherwise the dropdown renders underneath them.
    recipientWrapOpen: {zIndex: 1000, elevation: 14},
    recipientRow: {flexDirection: 'row', alignItems: 'flex-start'},
    recipientLabel: {fontSize: 15, fontWeight: '600', minWidth: 44, paddingTop: 7},
    recipientField: {flex: 1, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 6},
    recipientChip: {flexDirection: 'row', alignItems: 'center', borderRadius: 999, borderWidth: 1, paddingLeft: 4, paddingRight: 9, paddingVertical: 4, maxWidth: '92%'},
    recipientChipText: {fontSize: 13, fontWeight: '700'},
    recipientInput: {flex: 1, minWidth: 120, fontSize: 15, paddingVertical: 4},
    suggestBox: {position: 'absolute', top: '100%', left: 0, right: 0, borderRadius: 14, borderWidth: 1, paddingVertical: 6, zIndex: 999, elevation: 14, overflow: 'hidden'},
    suggestRow: {flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8},
    suggestName: {fontSize: 14, fontWeight: '700'},
    suggestEmail: {fontSize: 12, marginTop: 1},
    suggestType: {fontSize: 10.5, fontWeight: '800', textTransform: 'uppercase'},

    attachTile: {flexDirection: 'row', alignItems: 'center', borderRadius: 20, borderWidth: StyleSheet.hairlineWidth, padding: 12, marginBottom: 8, gap: 10},
    attachIcon: {width: 38, height: 38, borderRadius: 11, alignItems: 'center', justifyContent: 'center'},
    attachName: {fontSize: 13.5, fontWeight: '700'},
    attachMetaRow: {flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2},
    attachSize: {fontSize: 11.5},
    attachKind: {fontSize: 10, fontWeight: '900', textTransform: 'uppercase', letterSpacing: 0.5},
    attachAction: {width: 32, height: 32, borderRadius: 11, alignItems: 'center', justifyContent: 'center'},

    folderPill: {flexDirection: 'row', alignItems: 'center', height: 34, borderRadius: 999, borderWidth: 1, paddingHorizontal: 13, marginRight: 8},
    folderPillText: {fontSize: 13, fontWeight: '800', maxWidth: 130},
    folderDot: {width: 10, height: 10, borderRadius: 5, marginRight: 6},
    folderCount: {marginLeft: 7, borderRadius: 999, paddingHorizontal: 7, paddingVertical: 1, minWidth: 20, alignItems: 'center'},
    folderCountText: {fontSize: 11, fontWeight: '900'},

    fabWrap: {position: 'absolute', right: 18, bottom: 26},
    fab: {flexDirection: 'row', alignItems: 'center', justifyContent: 'center', height: 58, minWidth: 58, borderRadius: 29, paddingHorizontal: 20},
    fabGlow: {shadowColor: '#EA580C', shadowOffset: {width: 0, height: 10}, shadowOpacity: 0.36, shadowRadius: 16, elevation: 8},
    fabText: {color: '#FFFFFF', fontSize: 15, fontWeight: '800', marginLeft: 8},

    stripRow: {paddingHorizontal: 16, paddingVertical: 4, gap: 14},
    stripItem: {width: 62, alignItems: 'center'},
    stripRing: {width: 58, height: 58, borderRadius: 29, borderWidth: 2, alignItems: 'center', justifyContent: 'center'},
    stripDisc: {width: 46, height: 46, borderRadius: 23, borderWidth: StyleSheet.hairlineWidth, alignItems: 'center', justifyContent: 'center'},
    stripDot: {position: 'absolute', top: 2, right: 8, width: 12, height: 12, borderRadius: 6, borderWidth: 2},
    stripLabel: {fontSize: 11.5, fontWeight: '700', marginTop: 5, maxWidth: 62},
    assigneeLine: {flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 4},
    assigneeText: {fontSize: 12, fontWeight: '600', flexShrink: 1},
    acctChip: {flexDirection: 'row', alignItems: 'center', flexShrink: 1, gap: 9, paddingRight: 6},
    acctText: {flexShrink: 1, minWidth: 0},
    acctName: {fontSize: 15, fontWeight: '800'},
    acctEmail: {fontSize: 12, fontWeight: '600', marginTop: 1},

    writeBtn: {flexDirection: 'row', alignItems: 'center', gap: 6, height: 38, borderRadius: 999, paddingHorizontal: 13},
    writeBtnText: {color: '#FFFFFF', fontSize: 13, fontWeight: '800'},

    statCard: {flex: 1, borderRadius: 22, borderWidth: 1, paddingVertical: 12, paddingHorizontal: 13},
    statTop: {flexDirection: 'row', alignItems: 'center', gap: 8},
    statIcon: {width: 26, height: 26, borderRadius: 9, alignItems: 'center', justifyContent: 'center'},
    statValue: {fontSize: 19, fontWeight: '900'},
    statLabel: {fontSize: 11.5, fontWeight: '700', marginTop: 6},

    searchField: {flexDirection: 'row', alignItems: 'center', borderRadius: 999, borderWidth: 1, paddingHorizontal: 14, height: 44, gap: 8},
    searchFolderBtn: {paddingRight: 2},
    searchTap: {flex: 1, flexDirection: 'row', alignItems: 'center', gap: 9, height: '100%'},
    searchPlaceholder: {fontSize: 14.5, fontWeight: '600', flex: 1},
    searchAiInner: {width: 30, height: 30, borderRadius: 10, alignItems: 'center', justifyContent: 'center'},

    fchipRow: {gap: 8, paddingVertical: 2, paddingRight: 6},
    fchip: {flexDirection: 'row', alignItems: 'center', borderRadius: 999, borderWidth: 1, paddingHorizontal: 14, paddingVertical: 9},
    fchipText: {fontSize: 13, fontWeight: '800', maxWidth: 150},
    fchipCount: {marginLeft: 7, borderRadius: 999, paddingHorizontal: 7, paddingVertical: 1, minWidth: 20, alignItems: 'center'},
    fchipCountText: {fontSize: 11, fontWeight: '900'},

    remoteBanner: {flexDirection: 'row', alignItems: 'center', gap: 10, borderRadius: 14, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 10, marginBottom: 10},
    remoteIcon: {width: 30, height: 30, borderRadius: 10, alignItems: 'center', justifyContent: 'center'},
    remoteTextWrap: {flex: 1, minWidth: 0},
    remoteTitle: {fontSize: 12.5, fontWeight: '800'},
    remoteText: {fontSize: 11.5, fontWeight: '600', marginTop: 1},
    remoteActions: {flexDirection: 'row', alignItems: 'center', gap: 6},
    remoteBtn: {borderRadius: 999, borderWidth: 1, paddingHorizontal: 11, paddingVertical: 6},
    remoteBtnText: {fontSize: 12, fontWeight: '800'},

    skelAvatar: {width: 40, height: 40, borderRadius: 20},
    skelTopRow: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'},
    skelLine: {height: 10, borderRadius: 5, marginBottom: 7},
    skelBar: {height: 12, borderRadius: 6, marginBottom: 9},
});

export const mailUiStyles = styles;
